import { Google } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { json, redirect } from "@remix-run/node";
import type { LoaderFunction } from "@remix-run/node";
import { useSearchParams } from "@remix-run/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AuthPageLayout } from "~/components/AuthPageLayout";
import { getAuthData } from "~/ssr/auth.server";
import { addAuthBreadcrumb, captureAuthEvent } from "~/utils/authTelemetry";
import { supabase } from "~/utils/supabase.client";

export const loader: LoaderFunction = async ({ request }) => {
  const responseHeaders = new Headers();
  const authData = await getAuthData(request, responseHeaders);
  if (authData) {
    if (!authData.tosAgreed) {
      return redirect("/signup", { headers: responseHeaders });
    }
    const url = new URL(request.url);
    const teamId = url.searchParams.get("teamId");
    const dest = teamId ? `/select-team?teamId=${teamId}` : "/select-team";
    return redirect(dest, { headers: responseHeaders });
  }
  return json(null, { headers: responseHeaders });
};

export default function LoginPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirectTo");
  const teamId = searchParams.get("teamId");

  useEffect(() => {
    try {
      localStorage.removeItem("selectedTeamId");
      localStorage.removeItem("selectedProjectId");
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  const handleGoogleSignIn = async () => {
    addAuthBreadcrumb("oauth.signin_started");
    const callbackUrl = new URL(`${window.location.origin}/api/auth/callback`);

    // Only set "next" when there's a specific destination — callback defaults to /select-team
    const nextUrl = teamId ? `/select-team?teamId=${teamId}` : redirectTo;
    if (nextUrl) {
      callbackUrl.searchParams.set("next", nextUrl);
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: callbackUrl.toString(),
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
      },
    });
    if (error) {
      captureAuthEvent("auth.oauth_signin_failed", "error", {
        message: error.message,
      });
    }
  };

  return (
    <AuthPageLayout showFooterLinks>
      <Box sx={{ px: 3, py: 2 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: 24, sm: 32 },
            lineHeight: 1.33,
            letterSpacing: "-0.32px",
            color: "#28323c",
            mb: 1.5,
          }}
        >
          {t("auth.login.title")}
        </Typography>
        <Typography
          sx={{
            fontSize: 15,
            lineHeight: 1.6,
            color: "#374553",
          }}
        >
          {t("auth.login.description")}
        </Typography>
      </Box>

      <Box sx={{ px: 3, py: 2 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<Google />}
          onClick={handleGoogleSignIn}
          sx={{
            minHeight: 52,
            borderRadius: "8px",
            bgcolor: "#1868db",
            fontWeight: 600,
            fontSize: 16,
            textTransform: "none",
            "&:hover": { bgcolor: "#1356b5" },
          }}
        >
          {t("auth.login.button")}
        </Button>
      </Box>
    </AuthPageLayout>
  );
}
